/**
 * tRPC React client and QueryClient provider.
 * - Queries use httpBatchStreamLink (streaming) for fast parallel results
 * - Mutations use httpBatchLink (non-streaming) for reliable delivery
 * - Sets global React Query defaults (staleTime, refetchOnWindowFocus)
 * - anonymousProject.* ops always travel on their own batch link (never
 *   mixed with other namespaces) so middleware's isPublicTrpcPathname check
 *   cannot 401 the batch when protected ops tag along
 * - Non-anonymous ops are terminated client-side for signed-out visitors
 *   (TRPCClientError UNAUTHORIZED, no network request) so hooks land in
 *   their normal error state silently
 */
"use client";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { loggerLink, splitLink, httpBatchLink, httpBatchStreamLink } from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import { useState } from "react";
import SuperJSON from "superjson";

import type { AppRouter } from "@/lib/api/router-types";
import { sessionExpiredLink } from "@/lib/trpc/sessionExpiredLink";
import { createAuthFetch } from "@/lib/trpc/authFetch";
import {
  isAnonymousProjectOp,
  isPublicProcedureOp,
  signedOutGuardLink,
  shouldRetry,
} from "@/lib/trpc/anonTransportGuard";
import { POLLING } from "@/lib/timeouts";

const createQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        // Fixed at ACTIVE_MS (2s) so stale queries never trigger immediate
        // refetches between adaptive polling cycles (idle=30s, active=2s).
        // Polling via useEventPolling handles freshness; staleTime just
        // prevents redundant fetches on component remount/focus.
        staleTime: POLLING.ACTIVE_MS,
        refetchOnWindowFocus: false,
        // Suppress retries for locally-terminated UNAUTHORIZED errors
        // (signed-out visitors). Without this, React Query's default retry
        // (3 attempts) fires — each terminates instantly (no network), but
        // the attempts are unnecessary noise.
        retry: shouldRetry,
      },
    },
  });

let clientQueryClientSingleton: QueryClient | undefined = undefined;
const getQueryClient = () => {
  if (typeof window === "undefined") {
    // Server: always make a new query client
    return createQueryClient();
  }
  // Browser: use singleton pattern to keep the same query client
  return (clientQueryClientSingleton ??= createQueryClient());
};

export const api = createTRPCReact<AppRouter>();

export function TRPCReactProvider(props: { children: React.ReactNode }) {
  const queryClient = getQueryClient();

  const [trpcClient] = useState(() => {
    const linkConfig = {
      transformer: SuperJSON,
      url: getBaseUrl() + "/api/trpc",
      headers: () => {
        const headers = new Headers();
        headers.set("x-trpc-source", "nextjs-react");
        // Sent on every request, flag or no flag: harmless to the OFF path
        // (server reads it only under task-kanban) and keeps every React
        // Query key — including the ~10 exact-key getCurrentWeek
        // setData/getData/cancel optimistic writes — untouched, unlike
        // threading tz through an input field would.
        if (typeof Intl !== "undefined") {
          headers.set("x-lev-timezone", Intl.DateTimeFormat().resolvedOptions().timeZone);
        }
        return headers;
      },
      fetch: createAuthFetch(),
    };

    // Mutations use a non-streaming link for reliable delivery (streaming
    // connections drop silently on long-running ops like AI generation);
    // queries stream. Each call builds a FRESH pair of batch links so the three
    // routing branches below each own an isolated batch queue and never merge
    // their ops into one `/api/trpc/a,b` request.
    const makeBatch = () =>
      splitLink({
        condition: (op) => op.type === "mutation",
        true: httpBatchLink(linkConfig),
        false: httpBatchStreamLink(linkConfig),
      });

    return api.createClient({
      links: [
        loggerLink({
          enabled: () => false,
        }),
        sessionExpiredLink(),
        // Three isolated batch queues so anonymous/public ops never share a
        // batch with protected ops (a mixed batch 401s in middleware) and are
        // never terminated client-side by signedOutGuardLink:
        // - anonymousProject.* (LEV-1394): claim-cookie authed anon canvas.
        // - public procedures (share reads/comments, public YC app): need no
        //   Clerk session, so they bypass the guard and reach the server.
        // - everything else (protected): guarded — terminated locally for
        //   signed-out visitors to avoid 401 noise.
        splitLink({
          condition: isAnonymousProjectOp,
          true: makeBatch(),
          false: splitLink({
            condition: isPublicProcedureOp,
            true: makeBatch(),
            false: [signedOutGuardLink(), makeBatch()],
          }),
        }),
      ],
    });
  });

  return (
    <QueryClientProvider client={queryClient}>
      <api.Provider client={trpcClient} queryClient={queryClient}>
        {props.children}
      </api.Provider>
    </QueryClientProvider>
  );
}

function getBaseUrl() {
  if (typeof window !== "undefined") return window.location.origin;
  if (process.env.VERCEL_URL) return `https://${process.env.VERCEL_URL}`;
  return `http://localhost:${process.env.PORT ?? 3000}`;
}
